import React, { useState } from 'react';
import { PipelineCard } from '../../types/pipeline';
import Modal from '../common/Modal';
import {
  BuildingOfficeIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ClockIcon,
  TagIcon,
  PencilIcon,
  ArrowPathIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface PipelineCardModalProps {
  card: PipelineCard;
  onClose: () => void;
  onMove?: (cardId: string, fromStage: string, toStage: string) => void;
  onConvert?: (cardId: string, fromType: string, toType: string) => void;
  onEdit?: (card: PipelineCard) => void;
  onStatusChange?: (cardId: string, newStatus: string) => void;
}

const PipelineCardModal: React.FC<PipelineCardModalProps> = ({
  card,
  onClose,
  onMove,
  onConvert,
  onEdit,
  onStatusChange
}) => {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  // Dokumentum típus megjelenítés
  const getDocumentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      quotation: 'Árajánlat',
      order: 'Megrendelés',
      delivery_note: 'Szállítólevél',
      invoice: 'Számla'
    };
    return labels[type] || type;
  };

  // Státusz szín és ikon
  const getStatusConfig = (status: string) => {
    const configs: Record<string, { color: string; bg: string; icon: any }> = {
      draft: { color: 'text-gray-700', bg: 'bg-gray-100', icon: PencilIcon },
      sent: { color: 'text-blue-700', bg: 'bg-blue-100', icon: ArrowPathIcon },
      pending: { color: 'text-yellow-700', bg: 'bg-yellow-100', icon: ClockIcon },
      accepted: { color: 'text-green-700', bg: 'bg-green-100', icon: CheckCircleIcon },
      rejected: { color: 'text-red-700', bg: 'bg-red-100', icon: ClockIcon },
      confirmed: { color: 'text-green-700', bg: 'bg-green-100', icon: CheckCircleIcon },
      completed: { color: 'text-green-700', bg: 'bg-green-100', icon: CheckCircleIcon },
      paid: { color: 'text-green-700', bg: 'bg-green-100', icon: CheckCircleIcon },
      overdue: { color: 'text-red-700', bg: 'bg-red-100', icon: ClockIcon }
    };
    return configs[status] || configs.draft;
  };

  // Státusz fordítás
  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      draft: 'Piszkozat',
      sent: 'Elküldve',
      pending: 'Függőben',
      accepted: 'Elfogadva',
      rejected: 'Elutasítva',
      confirmed: 'Megerősítve',
      completed: 'Teljesítve',
      paid: 'Fizetve',
      overdue: 'Lejárt'
    };
    return labels[status] || status;
  };

  // Dátum formázás
  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    try {
      let d: Date;
      
      // Firestore Timestamp
      if (date.toDate && typeof date.toDate === 'function') {
        d = date.toDate();
      }
      // ISO string vagy Date string
      else if (typeof date === 'string') {
        if (date.trim() === '') return 'N/A';
        d = new Date(date);
      }
      // Date object
      else if (date instanceof Date) {
        d = date;
      }
      // Timestamp number
      else if (typeof date === 'number') {
        d = new Date(date);
      }
      else {
        return 'N/A';
      }
      
      // Ellenőrizzük, hogy valid-e a dátum
      if (isNaN(d.getTime())) return 'N/A';
      
      return d.toLocaleDateString('hu-HU', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Date formatting error:', error, date);
      return 'N/A';
    }
  };

  const statusConfig = getStatusConfig(card.status);
  const StatusIcon = statusConfig.icon;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title=""
      size="3xl"
    >
      <div className="space-y-6">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 -m-6 mb-6 p-6 rounded-t-lg border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <DocumentTextIcon className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{card.documentNumber}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{getDocumentTypeLabel(card.documentType)}</p>
                </div>
              </div>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${statusConfig.bg}`}>
              <StatusIcon className={`h-5 w-5 ${statusConfig.color}`} />
              <span className={`font-semibold ${statusConfig.color}`}>
                {getStatusLabel(card.status)}
              </span>
            </div>
          </div>
        </div>

        {/* Ügyfél és Összeg - Kiemelve */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
            <div className="flex items-center gap-2 mb-2">
              <BuildingOfficeIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-900 dark:text-blue-300">Ügyfél</span>
            </div>
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{card.customerName}</p>
          </div>

          {card.totalAmount !== undefined && (
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-100 dark:border-green-800">
              <div className="flex items-center gap-2 mb-2">
                <CurrencyDollarIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                <span className="text-sm font-medium text-green-900 dark:text-green-300">Összeg</span>
              </div>
              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {card.totalAmount.toLocaleString('hu-HU')} {card.currency || 'HUF'}
              </p>
            </div>
          )}
        </div>

        {/* Időpontok */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <CalendarIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Létrehozva</p>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">{formatDate(card.createdAt)}</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <ClockIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Frissítve</p>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">{formatDate(card.updatedAt)}</p>
            </div>
          </div>
        </div>

        {/* Megjegyzések */}
        {card.notes && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <DocumentTextIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              <span className="text-sm font-semibold text-amber-900 dark:text-amber-300">Megjegyzések</span>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
              {card.notes}
            </p>
          </div>
        )}

        {/* Címkék */}
        {card.tags && card.tags.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <TagIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Címkék</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {card.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm font-medium border border-purple-200 dark:border-purple-700"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Kapcsolódó dokumentumok */}
        {(card.quotationId || card.orderId || card.deliveryNoteId || card.invoiceId) && (
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <DocumentTextIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Kapcsolódó dokumentumok</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {card.quotationId && (
                <div className="flex items-center gap-2 p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Árajánlat</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{card.quotationId}</p>
                  </div>
                </div>
              )}
              {card.orderId && (
                <div className="flex items-center gap-2 p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Megrendelés</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{card.orderId}</p>
                  </div>
                </div>
              )}
              {card.deliveryNoteId && (
                <div className="flex items-center gap-2 p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Szállítólevél</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{card.deliveryNoteId}</p>
                  </div>
                </div>
              )}
              {card.invoiceId && (
                <div className="flex items-center gap-2 p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Számla</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{card.invoiceId}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Akciók */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Bezárás
          </button>
          <div className="flex gap-2 items-center">
            {/* Státusz váltás dropdown */}
            <div className="relative">
              <button
                onClick={() => onStatusChange ? setShowStatusMenu(!showStatusMenu) : null}
                disabled={!onStatusChange}
                className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  onStatusChange 
                    ? 'text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 hover:bg-purple-100 dark:hover:bg-purple-900/50' 
                    : 'text-gray-400 dark:text-gray-600 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 cursor-not-allowed'
                }`}
              >
                <ArrowPathIcon className="h-4 w-4" />
                Státusz váltás
              </button>
              
              {showStatusMenu && onStatusChange && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowStatusMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20">
                    {['draft', 'sent', 'pending', 'accepted', 'rejected', 'confirmed', 'completed', 'paid', 'overdue'].map((status) => {
                      const config = getStatusConfig(status);
                      const Icon = config.icon;
                      return (
                        <button
                          key={status}
                          onClick={() => {
                            onStatusChange(card.id, status);
                            setShowStatusMenu(false);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 ${
                            card.status === status ? 'bg-purple-50 dark:bg-purple-900/30' : ''
                          }`}
                        >
                          <Icon className={`h-4 w-4 ${config.color}`} />
                          <span className={config.color}>{getStatusLabel(status)}</span>
                          {card.status === status && (
                            <CheckCircleIcon className="h-4 w-4 ml-auto text-purple-600 dark:text-purple-400" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            
            {/* Szerkesztés gomb */}
            <button
              onClick={() => onEdit ? (onEdit(card), onClose()) : null}
              disabled={!onEdit}
              className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-all shadow-sm flex items-center gap-2 ${
                onEdit
                  ? 'text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700'
                  : 'text-gray-400 dark:text-gray-600 bg-gray-200 dark:bg-gray-800 cursor-not-allowed'
              }`}
            >
              <PencilIcon className="h-4 w-4" />
              Szerkesztés
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PipelineCardModal;
